import React, { useMemo } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Breadcrumbs,
  Link,
  Chip,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TimerIcon from '@mui/icons-material/Timer';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { useGetCourseAnalyticsQuery } from '../../../store/api/baseApi';
import KPICard from '../components/KPICard';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const CourseAnalyticsPage: React.FC = () => {
  const navigate = useNavigate();
  const { courseId } = useParams<{ courseId: string }>();

  const {
    data: courseData,
    isLoading,
    error,
  } = useGetCourseAnalyticsQuery(parseInt(courseId!), { skip: !courseId });

  // Performance trend chart data
  const performanceTrendData = useMemo(() => {
    if (!courseData || courseData.recent_performances.length === 0) return null;

    return {
      labels: courseData.recent_performances.map((p) =>
        format(new Date(p.date), 'MMM dd')
      ),
      datasets: [
        {
          label: 'Duration (minutes)',
          data: courseData.recent_performances.map((p) => (p.moving_time || 0) / 60),
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.1)',
          tension: 0.4,
          fill: true,
        },
      ],
    };
  }, [courseData]);

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'improving':
        return <TrendingUpIcon color="success" />;
      case 'declining':
        return <TrendingDownIcon color="error" />;
      case 'stable':
        return <TrendingFlatIcon color="warning" />;
      default:
        return null;
    }
  };

  const getTrendColor = (trend: string) => {
    switch (trend) {
      case 'improving':
        return 'success';
      case 'declining':
        return 'error';
      case 'stable':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error || !courseData) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error">
          Failed to load course analytics. Please try again later.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        sx={{ mb: 3 }}
      >
        <Link
          underline="hover"
          color="inherit"
          sx={{ cursor: 'pointer' }}
          onClick={() => navigate('/athlete/analytics/workouts')}
        >
          Workout Analytics
        </Link>
        <Typography color="text.primary">Course Analytics</Typography>
      </Breadcrumbs>

      {/* Course Header */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" alignItems="center" gap={2} mb={1}>
          <Typography variant="h4" component="h1" fontWeight="bold">
            {courseData.course_name}
          </Typography>
          <Chip
            label={courseData.progression_trend}
            color={getTrendColor(courseData.progression_trend) as any}
            icon={getTrendIcon(courseData.progression_trend) || undefined}
          />
        </Box>
        {courseData && (
          <Typography variant="body1" color="text.secondary">
            {courseData.completed_instances} of {courseData.total_instances} instances completed (
            {courseData.completion_rate.toFixed(0)}%)
          </Typography>
        )}
      </Box>

      {/* Performance Overview KPI Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <KPICard
            title="Total Instances"
            value={courseData.total_instances}
            icon={<FitnessCenterIcon />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <KPICard
            title="Completion Rate"
            value={`${courseData.completion_rate.toFixed(0)}%`}
            subtitle={`${courseData.completed_instances} completed`}
            icon={<CheckCircleIcon />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <KPICard
            title="Avg Distance"
            value={`${(courseData.average_distance / 1000).toFixed(1)} km`}
            icon={<DirectionsRunIcon />}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <KPICard
            title="Avg Duration"
            value={`${Math.round(courseData.total_time / courseData.completed_instances / 60)} min`}
            icon={<TimerIcon />}
            color="secondary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <KPICard
            title="Avg Pace"
            value={courseData.average_pace || 'N/A'}
            subtitle="Per km"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <KPICard
            title="Avg TSS"
            value={(courseData.average_tss || 0).toFixed(0)}
            subtitle="Training stress"
          />
        </Grid>
      </Grid>

      {/* Best Performance Card */}
      {courseData.best_performance && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <EmojiEventsIcon color="warning" fontSize="large" />
                <Typography variant="h6" fontWeight="bold">
                  Best Performance
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Date
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {format(new Date(courseData.best_performance.date), 'MMM dd, yyyy')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Distance
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {((courseData.best_performance.distance || 0) / 1000).toFixed(2)} km
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Pace
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {courseData.best_performance.pace || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Time
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {Math.floor((courseData.best_performance.moving_time || 0) / 60)} min
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Performance Trend Chart */}
      {performanceTrendData && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Performance Timeline
          </Typography>
          <Box sx={{ height: 400 }}>
            <Line
              data={performanceTrendData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { position: 'top' },
                  title: {
                    display: false,
                  },
                },
                scales: {
                  y: {
                    beginAtZero: false,
                    title: {
                      display: true,
                      text: 'Duration (minutes)',
                    },
                  },
                },
              }}
            />
          </Box>
        </Paper>
      )}

      {/* Recent Completions Table */}
      {courseData.recent_performances.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Recent Performances
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell align="right"><strong>Distance</strong></TableCell>
                  <TableCell align="right"><strong>Time</strong></TableCell>
                  <TableCell align="right"><strong>Pace</strong></TableCell>
                  <TableCell align="right"><strong>Avg HR</strong></TableCell>
                  <TableCell align="right"><strong>TSS</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {courseData.recent_performances.map((performance, index) => (
                  <TableRow
                    key={performance.activity_id}
                    sx={{
                      backgroundColor:
                        courseData.best_performance?.activity_id === performance.activity_id
                          ? 'rgba(255, 215, 0, 0.1)'
                          : 'transparent',
                    }}
                  >
                    <TableCell>
                      {format(new Date(performance.date), 'MMM dd, yyyy')}
                      {courseData.best_performance?.activity_id === performance.activity_id && (
                        <Chip
                          label="Best"
                          size="small"
                          color="warning"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {((performance.distance || 0) / 1000).toFixed(2)} km
                    </TableCell>
                    <TableCell align="right">
                      {Math.floor((performance.moving_time || 0) / 60)} min
                    </TableCell>
                    <TableCell align="right">{performance.pace || 'N/A'}</TableCell>
                    <TableCell align="right">
                      {performance.avg_hr ? `${performance.avg_hr} bpm` : 'N/A'}
                    </TableCell>
                    <TableCell align="right">
                      {performance.tss ? performance.tss.toFixed(0) : 'N/A'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Container>
  );
};

export default CourseAnalyticsPage;
